/**
 * /settings/extra-values (v9) — cadastro de Valores extras no padrão TiFlux:
 * breadcrumb Configurações / Financeiro / Valores extras, busca client-side,
 * "+ Valor extra" teal à direita e tabela Nome | Valor unitário | Descrição |
 * ações. Dialog: Nome* + Valor unitário* (R$) + Descrição (textarea). Leitura
 * livre p/ staff; escrita exige contracts.manage (botões escondidos).
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { usePermissions } from '@/auth/use-permissions';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import {
  matchesText,
  normalizeText,
  SettingsBreadcrumb,
  SettingsSearch,
} from '@/features/settings/settings-ui';
import type { ExtraValue } from '@erp/shared';
import { errorMessage, extraValuesOptions } from './billing-queries';
import { useDeleteExtraValue, useSaveExtraValue } from './billing-mutations';
import { formatBRL, formatBRLInput, parseBRLInput } from './billing-format';
import { MoneyInput } from './contract-drawer';

export function ExtraValuesSettingsPage() {
  const { can } = usePermissions();
  const canManage = can('contracts.manage');
  const listQ = useQuery(extraValuesOptions());
  const remove = useDeleteExtraValue();
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<{ extraValue: ExtraValue | null } | null>(null);

  const q = normalizeText(query.trim());
  const list = (listQ.data ?? []).filter(
    (v) => q === '' || matchesText(v.name, q) || matchesText(v.description ?? '', q)
  );

  return (
    <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      <SettingsBreadcrumb section="Financeiro" page="Valores extras" />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="text-xl leading-7 font-semibold">Valores extras</h1>
        <div className="ml-auto flex items-center gap-2">
          <SettingsSearch value={query} onChange={setQuery} label="Buscar valor extra" />
          {canManage ? (
            <Button size="sm" className="h-9" onClick={() => setDialog({ extraValue: null })}>
              <Plus className="size-4" strokeWidth={2.5} />
              Valor extra
            </Button>
          ) : null}
        </div>
      </div>

      <div className="mt-4">
        {listQ.isPending ? (
          <div className="space-y-2" aria-busy>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : listQ.isError ? (
          <EmptyState message={`Erro ao carregar valores extras: ${errorMessage(listQ.error)}`} />
        ) : list.length === 0 ? (
          <EmptyState
            className="h-52"
            message={q !== '' ? 'Nenhum valor extra encontrado' : 'Não há dados'}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-10 px-4 font-semibold">Nome</th>
                  <th className="h-10 w-44 px-4 font-semibold">Valor unitário</th>
                  <th className="h-10 px-4 font-semibold">Descrição</th>
                  <th className="h-10 w-24 px-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((v) => (
                  <tr
                    key={v.id}
                    className="h-11 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-72 truncate px-4 font-medium">{v.name}</td>
                    <td className="px-4 tabular-nums whitespace-nowrap">
                      {formatBRL(v.unitValue)}
                    </td>
                    <td className="max-w-96 truncate px-4" title={v.description ?? undefined}>
                      {v.description || '-'}
                    </td>
                    <td className="px-4">
                      {canManage ? (
                        <div className="flex items-center justify-end gap-0.5">
                          <DeskRowAction
                            label="Editar valor extra"
                            onClick={() => setDialog({ extraValue: v })}
                          >
                            <Pencil className="size-3.5" />
                          </DeskRowAction>
                          <DeskRowAction
                            label="Excluir valor extra"
                            destructive
                            disabled={remove.isPending}
                            onClick={() => {
                              if (window.confirm(`Excluir o valor extra "${v.name}"?`)) {
                                remove.mutate(v.id);
                              }
                            }}
                          >
                            <Trash2 className="size-3.5" />
                          </DeskRowAction>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {remove.error ? (
        <p className="mt-2 text-sm text-destructive">{errorMessage(remove.error)}</p>
      ) : null}

      {dialog ? (
        <ExtraValueDialog extraValue={dialog.extraValue} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

function ExtraValueDialog({
  extraValue,
  onClose,
}: {
  extraValue: ExtraValue | null;
  onClose: () => void;
}) {
  const save = useSaveExtraValue();

  const [name, setName] = useState(extraValue?.name ?? '');
  const [valueText, setValueText] = useState(
    extraValue ? formatBRLInput(extraValue.unitValue) : ''
  );
  const [description, setDescription] = useState(extraValue?.description ?? '');

  const unitValue = parseBRLInput(valueText);
  const valid = name.trim() !== '' && unitValue !== null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || unitValue === null) return;
    save.mutate(
      {
        id: extraValue?.id ?? null,
        body: {
          name: name.trim(),
          unitValue,
          description: description.trim() === '' ? null : description.trim(),
        },
      },
      { onSuccess: onClose }
    );
  };

  return (
    <Dialog open onClose={onClose} title={extraValue ? 'Editar valor extra' : 'Novo valor extra'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="extra-value-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="extra-value-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="extra-value-value">
            Valor unitário <span className="text-destructive">*</span>
          </Label>
          <MoneyInput id="extra-value-value" value={valueText} onChange={setValueText} required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="extra-value-description">Descrição</Label>
          <Textarea
            id="extra-value-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !valid}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
